import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Clock, CheckCircle2, User, Users, ArrowDown, BellRing } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

function parseDate(yyyymmdd) {
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    return new Date(y, m - 1, d);
}
function fmtFecha(yyyymmdd) {
    const d = parseDate(yyyymmdd);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return "Hoy";
    if (diff === 1) return "Mañana";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${WEEKDAYS[d.getDay()]} ${dd}/${mm}`;
}
function fmtHora(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Devuelve la etiqueta de autorización para mostrar a usuario y a recepción.
// Prioridad: campos nuevos (companions_mode + arrival_action). Fallback legacy
// (access_mode + arrival_decision) para visitas viejas.
export function getAccessLabel(v) {
    // Caso nuevo (visitas creadas con los dos campos separados)
    if (v.companions_mode || v.arrival_action) {
        const comp = v.companions_mode || "solo";
        const arr = v.arrival_action || "can_go_up";
        const compTxt = comp === "con_acompanantes" ? "Con acompañante/s" : "Viene solo/a";
        const arrTxt = arr === "wait_at_reception" ? "Espera en recepción" : "Que suba";
        const tone = arr === "wait_at_reception" ? "warn" : "ok";
        return { text: `${compTxt} · ${arrTxt}`, tone };
    }

    // Legacy (visitas viejas)
    const mode = v.access_mode || "notify_on_arrival";
    const decision = v.arrival_decision || (mode === "notify_on_arrival" ? "pending" : mode);
    if (mode === "solo") return { text: "Autorizada: sube sola", tone: "ok" };
    if (mode === "con_acompanantes") return { text: "Autorizada: sube con acompañantes", tone: "ok" };
    if (decision === "solo") return { text: "Autorizada: sube sola", tone: "ok" };
    if (decision === "con_acompanantes") return { text: "Autorizada: sube con acompañantes", tone: "ok" };
    if (decision === "wait_at_reception") return { text: "Espera en recepción", tone: "warn" };
    if (v.estado === "llego") return { text: "Esperando decisión", tone: "warn" };
    return { text: "Te avisan al llegar", tone: "muted" };
}

function AccessBadge({ v }) {
    const { text, tone } = getAccessLabel(v);
    const cls =
        tone === "ok"
            ? "bg-success/15 text-success border-success/40"
            : tone === "warn"
                ? "bg-warning/15 text-warning border-warning/50"
                : "bg-muted text-muted-foreground border-border";
    return (
        <span
            data-testid={`vis-access-label-${v.visita_id}`}
            className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${cls}`}
        >
            {text}
        </span>
    );
}

function VisitaCard({ v, onDelete, onDecide, deciding }) {
    const llego = v.estado === "llego";
    const needsDecision = llego && (v.access_mode || "notify_on_arrival") === "notify_on_arrival"
        && (v.arrival_decision || "pending") === "pending";
    return (
        <Card data-testid={`mi-vis-${v.visita_id}`}>
            <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                    <p className="font-heading font-semibold text-sm leading-tight truncate flex-1">
                        {v.nombre_visita}
                    </p>
                    {llego ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-success/15 text-success border border-success/50 shrink-0 whitespace-nowrap">
                            <CheckCircle2 className="h-2.5 w-2.5" /> Llegó
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-warning/15 text-warning border border-warning/60 shrink-0 whitespace-nowrap">
                            <Clock className="h-2.5 w-2.5" /> No llegó aún
                        </span>
                    )}
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground truncate">
                        Autorizado para: <span className={fmtFecha(v.fecha) === "Hoy" ? "text-foreground" : "text-primary"}>{fmtFecha(v.fecha)}</span>
                        {llego && v.hora_ingreso && (
                            <> · Llegó <span className="font-mono">{fmtHora(v.hora_ingreso)}</span></>
                        )}
                    </p>
                    <button
                        type="button"
                        onClick={onDelete}
                        data-testid={`del-vis-${v.visita_id}`}
                        className="text-muted-foreground hover:text-destructive p-1 -mr-1 shrink-0"
                        aria-label="Eliminar"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
                <div className="mt-2">
                    <AccessBadge v={v} />
                </div>

                {needsDecision && (
                    <div className="mt-3 rounded-md border border-warning/40 bg-warning/5 p-2.5">
                        <p className="text-[11px] font-semibold text-warning flex items-center gap-1.5 mb-2">
                            <BellRing className="h-3 w-3" /> Tu visita llegó. ¿Cómo la recibís?
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={deciding}
                                onClick={() => onDecide("solo")}
                                data-testid={`vis-decide-solo-${v.visita_id}`}
                                className="h-8 text-xs"
                            >
                                <User className="h-3.5 w-3.5 mr-1" /> Que suba sola
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={deciding}
                                onClick={() => onDecide("con_acompanantes")}
                                data-testid={`vis-decide-acomp-${v.visita_id}`}
                                className="h-8 text-xs"
                            >
                                <Users className="h-3.5 w-3.5 mr-1" /> Con acompañantes
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={deciding}
                                onClick={() => onDecide("wait_at_reception")}
                                data-testid={`vis-decide-wait-${v.visita_id}`}
                                className="h-8 text-xs"
                            >
                                <ArrowDown className="h-3.5 w-3.5 mr-1" /> Bajo a buscarla
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function MisVisitas() {
    const [items, setItems] = useState([]);
    const [delId, setDelId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [decidingId, setDecidingId] = useState(null);

    const load = () => api.get("/visitas/mias").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const remove = async () => {
        if (!delId) return;
        setBusy(true);
        try {
            await api.delete(`/visitas/${delId}`);
            setItems((arr) => arr.filter((x) => x.visita_id !== delId));
            setDelId(null);
        } catch (e) {
            toast.error(formatApiError(e));
            await load();
        } finally {
            setBusy(false);
        }
    };

    const decidir = async (visitaId, decision) => {
        setDecidingId(visitaId);
        // optimista
        setItems((arr) => arr.map((x) => x.visita_id === visitaId ? { ...x, arrival_decision: decision } : x));
        try {
            await api.post(`/visitas/${visitaId}/decision`, { decision });
        } catch (e) {
            toast.error(formatApiError(e));
            await load();
        } finally {
            setDecidingId(null);
        }
    };

    const { pendientes, llegaron } = useMemo(() => {
        const p = items.filter((v) => v.estado !== "llego");
        const l = items.filter((v) => v.estado === "llego");
        const keyPend = (v) => `${v.fecha} ${v.creado_en || ""}`;
        p.sort((a, b) => keyPend(a).localeCompare(keyPend(b)));
        l.sort((a, b) => (b.hora_ingreso || "").localeCompare(a.hora_ingreso || ""));
        return { pendientes: p, llegaron: l };
    }, [items]);

    return (
        <div className="max-w-2xl">
            <PageHeader title="Mis Visitas" subtitle="Tus próximas visitas." />

            {items.length === 0 ? (
                <EmptyState title="No tenés visitas próximas." description="Cargá una visita para verla acá." />
            ) : (
                <div className="space-y-5">
                    {pendientes.length > 0 && (
                        <section>
                            <h3 className="text-[11px] uppercase tracking-widest font-semibold text-warning mb-2 flex items-center gap-1.5">
                                <Clock className="h-3 w-3" /> No llegaron aún
                                <span className="text-muted-foreground font-mono ml-1">({pendientes.length})</span>
                            </h3>
                            <div className="grid gap-2">
                                {pendientes.map((v) => (
                                    <VisitaCard
                                        key={v.visita_id}
                                        v={v}
                                        onDelete={() => setDelId(v.visita_id)}
                                        onDecide={(d) => decidir(v.visita_id, d)}
                                        deciding={decidingId === v.visita_id}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {llegaron.length > 0 && (
                        <section>
                            <h3 className="text-[11px] uppercase tracking-widest font-semibold text-success mb-2 flex items-center gap-1.5">
                                <CheckCircle2 className="h-3 w-3" /> Llegaron
                                <span className="text-muted-foreground font-mono ml-1">({llegaron.length})</span>
                            </h3>
                            <div className="grid gap-2">
                                {llegaron.map((v) => (
                                    <VisitaCard
                                        key={v.visita_id}
                                        v={v}
                                        onDelete={() => setDelId(v.visita_id)}
                                        onDecide={(d) => decidir(v.visita_id, d)}
                                        deciding={decidingId === v.visita_id}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}

            <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Querés eliminar esta visita?</AlertDialogTitle>
                        <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            disabled={busy}
                            data-testid="del-vis-confirm"
                            className="bg-destructive hover:bg-destructive/90"
                        >
                            {busy ? "Eliminando…" : "Eliminar"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
